import { useQuery } from "@tanstack/react-query"
import { Users, TrendingUp, CreditCard, CheckCircle, Building2, ClipboardList, Wrench, ShieldCheck } from "lucide-react"
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"
import { format } from "date-fns"

import { api } from "@/lib/api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"

interface Stats {
  total_users: number
  verified_users: number
  active_subscriptions: number
  mrr: number
  arr: number
  total_properties: number
  active_leases: number
  open_maintenance: number
}

interface RevenueSnapshot {
  id: string
  snapshot_date: string
  mrr: string
  arr: string
  active_subscribers: number
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  color = "text-primary",
  bg = "bg-primary/10",
}: {
  icon: typeof Users
  label: string
  value: string | number
  sub?: string
  color?: string
  bg?: string
}) {
  return (
    <Card className="rounded-2xl border-border shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className={`h-10 w-10 rounded-xl ${bg} flex items-center justify-center ${color}`}>
            <Icon className="h-5 w-5" />
          </div>
          <span className="text-sm text-muted-foreground font-medium">{label}</span>
        </div>
        <p className="text-3xl font-bold tracking-tight">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>}
      </CardContent>
    </Card>
  )
}

export default function AdminDashboardPage() {
  const { data: stats, isLoading: statsLoading } = useQuery<Stats>({
    queryKey: ["admin", "stats"],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/stats")
      return data.data
    },
  })

  const { data: snapshots = [], isLoading: snapshotsLoading } = useQuery<RevenueSnapshot[]>({
    queryKey: ["admin", "revenue"],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/revenue")
      return (data.data ?? []).slice(0, 30).reverse()
    },
  })

  const chartData = snapshots.map((s) => ({
    date: format(new Date(s.snapshot_date), "MMM d"),
    MRR: parseFloat(s.mrr ?? "0"),
    Subscribers: s.active_subscribers,
  }))

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">System Overview</h1>
        <p className="text-muted-foreground mt-1">Platform health, revenue, and usage metrics.</p>
      </div>

      {/* Financial & User KPI Row */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Core Metrics</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {statsLoading ? (
            [1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)
          ) : (
            <>
              <StatCard
                icon={TrendingUp}
                label="Monthly Recurring Revenue"
                value={`€${(stats?.mrr ?? 0).toFixed(2)}`}
                bg="bg-emerald-500/10"
                color="text-emerald-600"
              />
              <StatCard
                icon={CheckCircle}
                label="Annual Run Rate"
                value={`€${(stats?.arr ?? 0).toFixed(2)}`}
                bg="bg-violet-500/10"
                color="text-violet-600"
              />
              <StatCard
                icon={CreditCard}
                label="Active Subscriptions"
                value={stats?.active_subscriptions ?? 0}
                sub="Paying customers"
                bg="bg-blue-500/10"
                color="text-blue-600"
              />
              <StatCard
                icon={Users}
                label="Total Users"
                value={stats?.total_users ?? 0}
                sub={`${stats?.verified_users ?? 0} verified accounts`}
                bg="bg-slate-500/10"
                color="text-slate-600"
              />
            </>
          )}
        </div>
      </div>

      {/* Platform Usage Row */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Platform Usage</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {statsLoading ? (
            [1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-2xl" />)
          ) : (
            <>
              <StatCard
                icon={Building2}
                label="Total Properties"
                value={stats?.total_properties ?? 0}
                bg="bg-orange-500/10"
                color="text-orange-600"
              />
              <StatCard
                icon={ClipboardList}
                label="Active Leases"
                value={stats?.active_leases ?? 0}
                bg="bg-pink-500/10"
                color="text-pink-600"
              />
              <StatCard
                icon={Wrench}
                label="Open Maintenance"
                value={stats?.open_maintenance ?? 0}
                sub="Pending resolution"
                bg="bg-red-500/10"
                color="text-red-600"
              />
            </>
          )}
        </div>
      </div>

      {/* Charts Row */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Revenue Growth</h2>
        {snapshotsLoading ? (
          <Skeleton className="h-[350px] w-full rounded-2xl" />
        ) : chartData.length > 0 ? (
          <Card className="rounded-2xl border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium text-muted-foreground">MRR (last 30 days)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData} margin={{ top: 5, right: 0, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="4 4" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                    tickLine={false}
                    axisLine={false}
                    dy={10}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                    tickFormatter={(v) => `€${v}`}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{ borderRadius: '12px', border: '1px solid hsl(var(--border))', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                    formatter={(v: number) => [`€${v.toFixed(2)}`, "MRR"]}
                  />
                  <Area
                    type="monotone"
                    dataKey="MRR"
                    stroke="#0ea5e9"
                    strokeWidth={3}
                    fill="url(#mrrGrad)"
                    activeDot={{ r: 6, strokeWidth: 0, fill: '#0ea5e9' }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-2xl border-border border-dashed bg-muted/30">
            <CardContent className="p-12 text-center flex flex-col items-center">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium">No Revenue Data Yet</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                The revenue chart will populate automatically once subscriptions are active and the daily snapshot job runs.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
